import { useCallback } from 'react'

import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import { DismissAllCommentsForChannelsMutation } from '../../../hooks/comments/useDismissAll'
import { getOneMonthAgoIso } from '../../../util/dateHelpers'

export type MonthlyCleanupResolveResult = {
  /** Channels whose old comments were dismissed successfully, in resolve order. */
  resolvedChannelIds: string[]
  /** True if at least one channel failed to resolve. */
  hasFailure: boolean
}

type UseMonthlyCleanupResolveReturn = {
  resolveChannels: (
    channelIds: string[],
    onChannelResolved?: (channelId: string) => void,
  ) => Promise<MonthlyCleanupResolveResult>
}

/**
 * Resolves (dismisses) each channel's unreplied comments older than one month,
 * one channel at a time. Resolving sequentially lets the cleanup dialog reveal
 * a checkmark per channel as it completes. Toasts are intentionally not fired
 * here — the cleanup flow reports progress and completion in its own dialog.
 */
export const useMonthlyCleanupResolve = (): UseMonthlyCleanupResolveReturn => {
  const [dismissAllForChannels] = useTypedMutation(
    DismissAllCommentsForChannelsMutation,
    (data) => data.dismissAllCommentsForChannels,
    {
      successTypename: 'DismissAllCommentsForChannelsSuccess',
      refetchQueries: ['Comments'],
    },
  )

  const resolveChannels = useCallback(
    async (
      channelIds: string[],
      onChannelResolved?: (channelId: string) => void,
    ): Promise<MonthlyCleanupResolveResult> => {
      const toDate = getOneMonthAgoIso()
      const resolvedChannelIds: string[] = []
      let hasFailure = false

      const resolveAtIndex = async (index: number): Promise<void> => {
        if (index >= channelIds.length) return

        const channelId = channelIds[index]
        if (!channelId) {
          await resolveAtIndex(index + 1)
          return
        }

        const result = await dismissAllForChannels({
          variables: { input: { channelIds: [channelId], toDate } },
        })

        if (result.success) {
          resolvedChannelIds.push(channelId)
          onChannelResolved?.(channelId)
        } else {
          hasFailure = true
        }

        await resolveAtIndex(index + 1)
      }

      await resolveAtIndex(0)

      return { resolvedChannelIds, hasFailure }
    },
    [dismissAllForChannels],
  )

  return { resolveChannels }
}
